import {
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ModalComponent } from '../../../shared/components/modal/modal';
import { Category, ClassificationType } from '../../../shared/models/category.models';
import { CategoryService } from '../../../core/services/category.service';
import { ToastService } from '../../../core/services/toast.service';
import { apiErrorMessage } from '../../../shared/utils/api-error';

@Component({
  selector: 'app-edit-category-modal',
  imports: [ModalComponent],
  templateUrl: './edit-category-modal.html',
})
export class EditCategoryModalComponent implements OnInit {
  category = input.required<Category>();
  closed = output<void>();
  saved = output<void>();
  removed = output<void>();

  private categoryService = inject(CategoryService);
  private toastService = inject(ToastService);
  private destroyRef = inject(DestroyRef);

  private initialName = '';

  protected categoryName = signal('');
  protected showErrors = signal(false);
  protected submitting = signal(false);
  protected confirmRemove = signal(false);
  protected removing = signal(false);
  protected hasChanges = computed(() => this.categoryName().trim() !== this.initialName);

  ngOnInit(): void {
    this.initialName = this.category().categoryName;
    this.categoryName.set(this.category().categoryName);
  }

  protected onSubmit(): void {
    this.showErrors.set(true);
    if (!this.categoryName().trim()) return;

    this.submitting.set(true);
    const payload = { id: this.category().id, name: this.categoryName().trim() };
    const request$ =
      this.category().classificationType === ClassificationType.Situation
        ? this.categoryService.updateSituationCategory(payload)
        : this.categoryService.updateResponseCategory(payload);

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.toastService.show('Category updated successfully');
        this.saved.emit();
      },
      error: (err) => {
        this.toastService.show(apiErrorMessage(err, 'Failed to update category'), 'error');
        this.submitting.set(false);
      },
    });
  }

  protected onRemove(): void {
    if (!this.confirmRemove()) {
      this.confirmRemove.set(true);
      return;
    }
    this.removing.set(true);
    const request$ =
      this.category().classificationType === ClassificationType.Situation
        ? this.categoryService.deleteSituationCategory(this.category().id)
        : this.categoryService.deleteResponseCategory(this.category().id);

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.toastService.show('Category deleted successfully');
        this.removed.emit();
      },
      error: (err) => {
        this.toastService.show(apiErrorMessage(err, 'Failed to delete category'), 'error');
        this.removing.set(false);
        this.confirmRemove.set(false);
      },
    });
  }
}
